import React from 'react';
import { Theme, styled } from '@mui/material/styles';
import TrimbleMaps, { LngLatBoundsLike } from '@trimblemaps/trimblemaps-js';
import { AllGeoJSON, bbox } from '@turf/turf';

import { Lane } from '../../interfaces/services/lanes';
import { MapLayers, MapSources } from '../../utils/maps/enums';
import {
    fitMapToBounds,
    handleLineStringPopupWithDescriptionMouseEnter,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
    trimbleMapsPopup,
} from '../../utils/maps/mapUtils';
import { addLaneCenterLayer, addLaneLineLayer, addLaneLineHoverLayer } from '../../utils/maps/layers/lanes';
import { addLanesSource, LanesMapSourceProperties } from '../../utils/maps/sources/lanes';
import MapSpinner from '../Loaders/MapSpinner';

import '../../assets/CSS/trimbleMaps.css';

const classesPrefix = 'laneMap';

const classes = {
    map: `${classesPrefix}-map`,
};

const StyledDiv = styled('div')(() => {
    return {
        [`&.${classes.map}`]: {
            height: '100%',
            minHeight: '300px',
        },
    };
});

interface LaneMapProps {
    /** Indicator to show loading spinner while data is fetching. */
    isFetchingData: boolean;
    /** MUI theme for styling the map layers */
    theme: Theme;
    /** Lanes to add to the map */
    lanes: Lane[];
    /** Hovered lane id to highlight */
    hoveredLaneId: string | null;
    /** Function to handle hovering a lane */
    handleHoveredLaneChange: (laneId: string | null) => void;
    /** Function to handle clicking a lane */
    handleLaneClick: (laneId: string) => void;
    /** Function to handle clicking an area */
    handleAreaClick: (areaId: string) => void;
    /** Indicator to trigger a map resize function. */
    resize?: boolean;
}

interface LaneMapState {
    mapStyleLoaded: boolean;
}

let map: TrimbleMaps.Map | null = null;
const mapContainer = React.createRef<HTMLDivElement>();

class LaneMap extends React.Component<LaneMapProps, LaneMapState> {

    constructor(props: LaneMapProps) {
        super(props);

        TrimbleMaps.APIKey = '173580F5C6F48F44B5006F78909958C1';

        this.state = {
            mapStyleLoaded: false,
        };
    }

    componentDidMount(): void {
        const latitude = 39.828347;
        const longitude = -98.579488;

        if (!mapContainer.current) { return; }

        map = new TrimbleMaps.Map({
            container: mapContainer.current, // Ties to an HTML DOM element
            center: [longitude, latitude], // Sets initial center of map
            style: TrimbleMaps.Common.Style.DATADARK, // Sets initial map style
            minZoom: 1.55, // To stop the zoom from showing multiple worlds
            zoom: 3, // Sets initial zoom level
        }).addControl(
            // Adds the zoom in/out and compass buttons
            new TrimbleMaps.NavigationControl()
        ).addControl(
            new TrimbleMaps.FullscreenControl()
        );

        map.setRegion(TrimbleMaps.Common.Region.WW);

        // README: The event `style.load` is no longer a public event of mapbox. We will need to use some combination of using `styledata` and `map.isStyleLoaded()` at some point.
        // However, these do not work the same and there are several open issues with mapbox that have yet to be resolved, such as this one:
        // https://github.com/mapbox/mapbox-gl-js/issues/9779
        // Once a map style is loaded, add any layers that were on back to the map
        map.on('style.load', (): void => {
            // The style.load event can fire after the component has been unmounted.
            if (map === null) {
                return;
            }

            this.removeLanesEventHandlers();
            this.addLanesToMap();

            // HACK: trigger a resize function to make map take up full width
            map.resize();
            this.setState({ mapStyleLoaded: true });
        });
    }

    componentDidUpdate(prevProps: LaneMapProps): void {
        // if the map is null, hold any CDU updates
        if (map === null) {
            return;
        }

        if (this.state.mapStyleLoaded === false) {
            return;
        }

        if (prevProps.hoveredLaneId !== this.props.hoveredLaneId) {
            this.addLanesSourceToMap();
        }

        // add lanes to map if props have updated
        if (prevProps.lanes !== this.props.lanes) {
            this.removeLanesEventHandlers();
            this.addLanesToMap();
        }

        // if resize prop is passed and is true force map to resize.
        if (this.props.resize === true) {
            map.resize();
        }
    }

    componentWillUnmount(): void {
        this.removeLanesEventHandlers();
    }

    handleLaneLineLayerMouseenter = (e: any): void => {
        if (map) {
            // Get the layer that was entered
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the hover function
            if (currentFeature.layer.id === MapLayers.laneLineHoverLayer) {
                if (e.features.length > 0) {
                    const { properties }: { properties: LanesMapSourceProperties; } = e.features[0];

                    // update the hovered lane id
                    this.props.handleHoveredLaneChange(properties.id);

                    // show the line popup
                    handleLineStringPopupWithDescriptionMouseEnter(map, e, properties.description, trimbleMapsPopup);
                }
            }
        }
    };

    handleLaneLineLayerMouseleave = (): void => {
        if (map) {
            // reset the hovered lane id
            this.props.handleHoveredLaneChange(null);

            // remove the line popup
            handlePopupMouseLeave(map, trimbleMapsPopup);
        }
    };

    handleLaneLineLayerClick = (e: any): void => {
        if (map) {
            // Get the layer that was clicked
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the click function
            if (currentFeature.layer.id === MapLayers.laneLineHoverLayer) {
                // pull custom properties off the geoJson obj to send to parent
                const { properties }: { properties: LanesMapSourceProperties; } = e.features[0];
                // handle action from parent component
                this.props.handleLaneClick(properties.id);
            }
        }
    };

    handleLaneCenterLayerClick = (e: any): void => {
        if (map) {
            // Get the layer that was clicked
            const features = map.queryRenderedFeatures(e.point);
            const currentFeature = features[0];

            // If it was this layer, fire the click function
            if (currentFeature.layer.id === MapLayers.laneCenterLayer) {
                // pull custom properties off the geoJson obj to send to parent
                const { properties }: { properties: LanesMapSourceProperties; } = e.features[0];
                // handle action from parent component
                this.props.handleAreaClick(properties.id);
            }
        }
    };

    addLanesSourceToMap = (): void => {
        if (map) {
            // add map source
            addLanesSource({
                map,
                lanes: this.props.lanes || [],
                hoveredLaneId: this.props.hoveredLaneId,
                theme: this.props.theme,
            });
        }
    };

    addLanesToMap = (): void => {
        if (map) {
            const lanesSource = addLanesSource({
                map,
                lanes: this.props.lanes || [],
                hoveredLaneId: this.props.hoveredLaneId,
                theme: this.props.theme,
            });

            // add lane lines
            addLaneLineLayer({
                map,
                mapSource: MapSources.lanesSource,
                isVisible: true,
            });

            // add lane lines
            addLaneLineHoverLayer({
                map,
                mapSource: MapSources.lanesSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseenter);
            map.on('mouseleave', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseleave);
            map.on('click', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerClick);

            // add lane centers
            addLaneCenterLayer({
                map,
                mapSource: MapSources.lanesSource,
                isVisible: true,
            });
            map.on('mouseenter', MapLayers.laneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
            map.on('mouseleave', MapLayers.laneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
            map.on('click', MapLayers.laneCenterLayer, this.handleLaneCenterLayerClick);

            const { data } = lanesSource;
            if ((data as GeoJSON.FeatureCollection<GeoJSON.Point | GeoJSON.LineString, LanesMapSourceProperties>).features.length > 0) {
                const boundingBox = bbox(data as AllGeoJSON);
                fitMapToBounds({
                    map,
                    bounds: boundingBox as LngLatBoundsLike,
                    zoomMap: true,
                    maxZoom: 12,
                });
            }
        }
    };

    removeLanesEventHandlers = (): void => {
        if (map) {
            if (map.getLayer(MapLayers.laneLineHoverLayer) !== undefined) {
                map.off('mouseenter', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseenter);
                map.off('mouseleave', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerMouseleave);
                map.off('click', MapLayers.laneLineHoverLayer, this.handleLaneLineLayerClick);
            }

            if (map.getLayer(MapLayers.laneCenterLayer) !== undefined) {
                map.off('mouseenter', MapLayers.laneCenterLayer, (e): void => { handlePopupMouseEnter(map, e, trimbleMapsPopup); });
                map.off('mouseleave', MapLayers.laneCenterLayer, (): void => { handlePopupMouseLeave(map, trimbleMapsPopup); });
                map.off('click', MapLayers.laneCenterLayer, this.handleLaneCenterLayerClick);
            }
        }
    };

    render(): React.JSX.Element {
        return (
            <StyledDiv
                id='trimble-map-container'
                ref={mapContainer}
                className={classes.map}
                data-qa='laneMap-container'
            >
                {
                    this.props.isFetchingData &&
                    <MapSpinner />
                }
            </StyledDiv>
        );
    }

}

export default LaneMap;
